import React, { useState, useRef } from 'react';
import emailjs from '@emailjs/browser';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SEOHead from '../components/SEOHead';
import { Phone, Mail, MapPin, Send, CircleCheck as CheckCircle, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';

const Contact = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    nachricht: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Kontakt – Schteffann IT-Dienstleistungen Mönchengladbach',
    description: 'Kontakt zu Stephan Schunter: Kostenloses Erstgespräch für IT-Dienstleistungen, Microsoft 365, Digitalisierung. Mönchengladbach.',
    url: 'https://schteffann.de/contact'
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');

    // console.log('EmailJS Service ID:', process.env.REACT_APP_EMAILJS_SERVICE_ID);
    // console.log('EmailJS Template ID:', process.env.REACT_APP_EMAILJS_TEMPLATE_ID);
    // console.log('EmailJS Public Key:', process.env.REACT_APP_EMAILJS_PUBLIC_KEY);
    // console.log('FormData: ', formRef.current);
    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          telefon: formData.telefon,
          nachricht: formData.nachricht,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setSubmitted(true);
    } catch (err) {
      console.error('EmailJS Fehler:', err);
      setError('Beim Senden ist ein Fehler aufgetreten...');
    } finally {
      setSending(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Kontakt – IT-Dienstleistungen Mönchengladbach | Schteffann"
        description="Kontakt zu Stephan Schunter: Kostenloses Erstgespräch für IT-Dienstleistungen, Microsoft 365, Digitalisierung. Mönchengladbach, Viersen, Krefeld."
        keywords="Kontakt IT Mönchengladbach, IT Beratung anfragen, Schteffann Kontakt, Stephan Schunter"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              Kontakt
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Lassen Sie uns </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">reden</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Kostenloses Erstgespräch, unverbindlich und ehrlich. Ich schaue mir Ihre Situation an und sage Ihnen, was ich tun kann.
            </p>
          </div>
        </div>
      </section>

      {/* Kontakt */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Kontaktinfo */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Kontaktmöglichkeiten</h2>
              
              <div className="space-y-6">
                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                        <Phone className="h-6 w-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Telefon</h3>
                        <a href="tel:015679697252" className="text-cyan-600 hover:text-cyan-700 text-lg">
                          0156-79697252
                        </a>
                        <p className="text-sm text-slate-600 mt-1">Mo–Fr, 9–18 Uhr</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                        <Mail className="h-6 w-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">E-Mail</h3>
                        <a href="mailto:kontakt@schteffann.de" className="text-cyan-600 hover:text-cyan-700 text-lg">
                          kontakt@schteffann.de
                        </a>
                        <p className="text-sm text-slate-600 mt-1">Antwort innerhalb von 24 Stunden</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl">
                        <MapPin className="h-6 w-6 text-cyan-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 mb-1">Standort</h3>
                        <p className="text-slate-700">Mönchengladbach und Umgebung</p>
                        <p className="text-sm text-slate-600 mt-1">Radius ca. 20 km – inkl. Viersen, Krefeld, Korschenbroich, Jüchen</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 p-6 bg-gradient-to-br from-slate-50 to-cyan-50/30 rounded-lg">
                <h3 className="font-bold text-slate-900 mb-3">Was passiert nach Ihrer Nachricht?</h3>
                <div className="space-y-2 text-slate-700">
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-cyan-600 mt-0.5" />
                    <p>Ich melde mich innerhalb von 24 Stunden</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-cyan-600 mt-0.5" />
                    <p>Wir vereinbaren ein kostenloses Erstgespräch (telefonisch oder vor Ort)</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-cyan-600 mt-0.5" />
                    <p>Ich höre mir Ihre Situation an und gebe eine ehrliche Einschätzung</p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle className="h-5 w-5 text-cyan-600 mt-0.5" />
                    <p>Kein Verkaufsgespräch, keine Verpflichtung</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Formular */}
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-8">Nachricht senden</h2>
              
              {!submitted ? (
                <Card className="border-slate-200 shadow-lg">
                  <CardContent className="p-8">
                    <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                          Name *
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Ihr Name"
                          className="w-full"
                          data-testid="contact-name-input"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                          E-Mail *
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="ihre@email.de"
                          className="w-full"
                          data-testid="contact-email-input"
                        />
                      </div>

                      <div>
                        <label htmlFor="telefon" className="block text-sm font-medium text-slate-700 mb-2">
                          Telefon (optional)
                        </label>
                        <Input
                          id="telefon"
                          name="telefon"
                          type="tel"
                          value={formData.telefon}
                          onChange={handleChange}
                          placeholder="0156..."
                          className="w-full"
                          data-testid="contact-telefon-input"
                        />
                      </div>

                      <div>
                        <label htmlFor="nachricht" className="block text-sm font-medium text-slate-700 mb-2">
                          Ihre Nachricht *
                        </label>
                        <Textarea
                          id="nachricht"
                          name="nachricht"
                          required
                          value={formData.nachricht}
                          onChange={handleChange}
                          placeholder="Erzählen Sie kurz, worum es geht..."
                          rows={6}
                          className="w-full"
                          data-testid="contact-nachricht-input"
                        />
                      </div>

                      {error && (
                        <div className="flex items-start space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="contact-error-message">
                          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      )}

                      <Button
                        type="submit"
                        size="lg"
                        disabled={sending}
                        className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white disabled:opacity-50"
                        data-testid="contact-submit-button"
                      >
                        {sending ? (
                          <>
                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                            Wird gesendet...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-5 w-5" />
                            Nachricht senden
                          </>
                        )}
                      </Button>

                      <p className="text-xs text-slate-500 text-center">
                        Ihre Daten werden vertraulich behandelt und nicht weitergegeben.
                      </p>
                    </form>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50 shadow-lg">
                  <CardContent className="p-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full mb-6">
                      <CheckCircle className="h-8 w-8 text-cyan-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">Vielen Dank!</h3>
                    <p className="text-lg text-slate-700 mb-6">
                      Ihre Nachricht ist bei mir angekommen. Ich melde mich innerhalb von 24 Stunden bei Ihnen.
                    </p>
                    <p className="text-slate-600">
                      Stephan Schunter
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;